import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate, useParams } from 'react-router-dom'
import get from 'lodash/get'

import {
  GlobalIsaakLinks,
  IsaacLinks,
  Navigation,
  ProjectInformation,
  ProjectDetail,
  ProjectDetailForm,
  ProjectDetailFormSkeleton
} from 'modules/ProjectEdit/components'
import { useArtistInfo } from 'modules/Projects/serviceHooks'
import { useProject, useValidation } from 'modules/ProjectEdit/serviceHooks'
import { IIsaacLink } from 'shared/interfaces'
import { formatProjectInfo } from 'shared/utils/quillForm'
import { useLoading } from 'shared/hooks'
import { TRouterParams } from 'shared/types'
import { formatToOptionsById } from 'shared/utils/labels'
import { ProductTableContainer } from './ProductTableContainer'
import { IProductForm } from '../components/ProductTable'
import { ManagementMenu } from '../components/ManagementMenu'
import { asyncProjectSchema, productSchema } from '../constants'
import { updateISAACLink } from 'shared/utils/updateISAACLink'
import { useGpsUnits, useLocalProjectFlags, useProjectParent } from 'modulesCommon/serviceHooks'
import { GlobalProjectInfo } from 'components'

import { IProjectForm } from '../types'
import { useZoneTemplates } from '../../Labels/serviceHooks/useGetZoneTemplates'
import { LabelTemplateType } from 'shared/__gql-types'
import { ENABLED_FEATURES } from 'shared/utils/featureFlags'

function getInfo(value: string | undefined, defaultValue: string): string {
  if (typeof value === 'string') return formatProjectInfo(value)
  return defaultValue
}

const ProjectEditContainer = () => {
  const { id } = useParams<TRouterParams>()

  const { updateProject, getUser } = useProject(id)

  const { units, activeProject, activeArtist, campaignLoading } = useArtistInfo(id)
  const navigate = useNavigate()
  const { isLocalized } = useLocalProjectFlags(activeProject)
  const parentProject = useProjectParent(activeProject)
  const { getTemplateByType } = useZoneTemplates(`${activeProject?.label_id}`)
  const { gpsUnitsLoading, gpsUnits } = useGpsUnits()

  const unitsOptions = useMemo(() => units.map(formatToOptionsById), [units])

  const defaultValues = useMemo<IProjectForm>(
    () => ({
      id: id as string,
      name: activeProject?.name ?? '',
      opusId: activeArtist?.opus_id ?? '',
      unitId: (activeProject?.unit_id ?? '').toString(),
      ownerId: activeProject?.owner_id,
      ownerEmail: get(activeProject, ['user', 'email'], '') || '',
      gpsStatus: activeArtist?.gps_status ?? '',
      isaacUrl: activeProject?.isaac_url?.map(({ title, url }: IIsaacLink) => ({ title, url })) ?? [],
      info: getInfo(activeProject?.info, getTemplateByType(LabelTemplateType.CampaignInfo)),
      teamInfo: getInfo(activeProject?.team_info, getTemplateByType(LabelTemplateType.TeamInfo)),
      artistId: Number(activeProject?.artist?.id),
      searchedName: activeArtist?.name ?? '',
      artistLabelId: activeArtist?.label_id ?? ''
    }),
    [id, activeProject, activeArtist?.opus_id, activeArtist?.gps_status, activeArtist?.label_id, getTemplateByType, activeArtist?.name]
  )

  const methods = useForm<IProjectForm>({
    mode: 'onChange',
    defaultValues: defaultValues,
    resolver: yupResolver(asyncProjectSchema({ checkUserEmail: getUser }))
  })

  const { handleValidate } = useValidation()

  const productFormMethods = useForm<IProductForm>({
    resolver: yupResolver(productSchema(handleValidate))
  })

  const onGoBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const onSave = useCallback<(data: IProjectForm) => Promise<void>>(
    async ({ searchedName, ...data }) => {
      await updateProject({
        ...data,
        artistLabelId: `${data.artistLabelId ?? activeProject.label_id ?? ''}`,
        isaacUrl: updateISAACLink(data.isaacUrl)
      })
      navigate(`/projects/${data.id}`)
    }, [navigate, updateProject, activeProject]
  )

  useEffect(() => {
    methods.reset(defaultValues)
  }, [defaultValues, methods])

  useLoading({ isLoading: gpsUnitsLoading })

  return (
    <div className="project-edit">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSave)}>
          <Navigation onGoBack={onGoBack}>
            <ManagementMenu
              isActive={activeProject?.active === true}
              id={id as string}
              onComplete={() => navigate('/projects', { replace: true })}
            />
          </Navigation>
          <ProjectDetail facebookId={activeArtist?.facebook_id} spotifyId={activeArtist?.spotify_id}>
            {campaignLoading ?
              <ProjectDetailFormSkeleton /> :
              <ProjectDetailForm control={methods.control} artist={activeArtist} gpsUnits={gpsUnits} unitOptions={unitsOptions}/>
            }
          </ProjectDetail>
          {isLocalized &&
            <GlobalIsaakLinks isaakLinks={isLocalized && parentProject?.isaac_url ? parentProject?.isaac_url : []} />}
          <IsaacLinks isLocalizing={isLocalized} />
          {ENABLED_FEATURES.GLOBALHUB && isLocalized && <GlobalProjectInfo />}
          <ProjectInformation />
        </form>
      </FormProvider>
      {
        !campaignLoading && (
          <FormProvider {...productFormMethods}>
            <ProductTableContainer artistName={activeArtist?.name} opusId={activeArtist?.opus_id ?? ''} />
          </FormProvider>
        )
      }
    </div>
  )
}

export default ProjectEditContainer
